import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

const NotFound = () => {
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.05),transparent_50%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(99,102,241,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(99,102,241,0.02)_1px,transparent_1px)] bg-[size:14px_24px]" />
      
      <div className="relative max-w-2xl mx-auto px-4 py-8 text-center">
        {/* Header decoration */}
        <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
          [SYSTEM::ERROR_404]
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative"
        >
          {/* Error Icon */}
          <div className="mb-8 relative">
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
                opacity: [0.5, 1, 0.5],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "linear"
              }}
              className="absolute -inset-4 rounded-full bg-red-500/10 blur-xl"
            />
            <ExclamationTriangleIcon className="w-16 h-16 text-red-400 mx-auto" />
          </div>

          {/* Error Code */}
          <div className="mb-4 font-mono">
            <div className="text-xs text-indigo-500/60 mb-1">ERROR_CODE::</div>
            <div className="text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-red-500 via-red-400 to-red-500">
              404
            </div>
          </div>

          {/* Error Message */}
          <div className="space-y-4 mb-8">
            <h1 className="text-2xl font-bold text-white font-mono">
              Connection Lost: Resource Not Found
            </h1>
            <div className="text-slate-400 font-mono">
              <div className="text-xs text-indigo-500/60 mb-2">[ERROR_DETAILS]</div>
              The requested resource could not be located on the secure network.
              <br />
              Please verify your access coordinates and try again.
            </div>
          </div>

          {/* Terminal-style Error Log */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-slate-800/50 rounded-lg border border-slate-700/50 p-4 mb-8 font-mono text-sm"
          >
            <div className="text-red-400">&gt;&gt; ERROR_LOG::</div>
            <div className="text-slate-400">Attempting to establish connection...</div>
            <div className="text-slate-400">Scanning network nodes...</div>
            <div className="text-red-400">! Resource not found in network</div>
            <div className="text-indigo-400">? Suggested action: Return to secure base</div>
          </motion.div>

          {/* Return Home Button */}
          <Link to="/">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="relative group inline-flex items-center gap-2"
            >
              <div className="absolute inset-0 bg-indigo-500/20 rounded backdrop-blur-sm group-hover:bg-indigo-500/30 transition-colors" />
              <div className="relative px-6 py-3 text-indigo-400 border border-indigo-500/50 rounded 
                group-hover:border-indigo-400 transition-colors font-mono"
              >
                <span className="flex items-center gap-2">
                  <ArrowLeftIcon className="w-4 h-4" />
                  <span>Return to Secure Base</span>
                  <span className="text-xs opacity-60">[0x1F4]</span>
                </span>
              </div>
            </motion.button>
          </Link>
        </motion.div>

        {/* Footer decoration */}
        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
          [END::ERROR_404]
        </div>
      </div>

      {/* Animated Background Elements */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 180, 360],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear"
        }}
        className="absolute top-1/4 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl -z-10"
      />
      <motion.div
        animate={{
          scale: [1.2, 1, 1.2],
          rotate: [0, -180, -360],
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: "linear"
        }}
        className="absolute bottom-1/4 left-0 w-96 h-96 bg-red-500/10 rounded-full blur-3xl -z-10"
      />
    </div>
  );
};

export default NotFound;